import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Download, Globe2, Smartphone, X } from "lucide-react";
import PublicLayout from "@/components/site/PublicLayout";
import { SHIELD_DOWNLOAD, SHIELD_STORE_URL } from "@/lib/shield";

// The public explanation of the Shield (browser extension now, Android app later): how to get it,
// what it records, every permission and why, and who sees what. It is the extension's privacy page
// in the Chrome Web Store and Edge Add-ons listings, and the extension's welcome page links here,
// so keep it in step with extension/ (and mobile/modules/hustlrmate-shield/ once the app ships).
const SUPPORT_EMAIL = "support.hustlrmate@gmail.com";

const HALVES = [
  {
    icon: Globe2, name: "Website Shield", where: "A browser extension for Chrome, Edge and other Chromium browsers",
    steps: [
      "Install the HustlrMate Shield extension (below).",
      "On hustlrmate.in, open Shield, agree to what it records and press Connect this browser. Until then it does nothing.",
      "List the sites to block outright or cap per day, for example 30 minutes of YouTube on weekdays.",
      "A blocked site is replaced with a HustlrMate page. You can ask for more time there; it's recorded.",
    ],
  },
  {
    icon: Smartphone, name: "App Shield", where: "Coming soon, with the HustlrMate Android app", soon: true,
    steps: [
      "In the app, open You › Shield and read what it collects. Nothing starts until you agree.",
      "Grant two Android permissions in system settings: usage access and display over other apps.",
      "Pick apps to block or cap per weekday, then press Turn on the Shield.",
      "When a blocked app opens, the Shield's block screen covers it. Android shows a \"Shield is on\" notification the whole time.",
    ],
  },
];

const RECORDS = [
  "Which site (youtube.com) or app (Instagram) you used",
  "Seconds spent on it each day",
  "How many times it was opened",
  "How many times the Shield blocked it",
  "Extra time you asked for, and the reason you typed",
  "Whether the Shield is on, and its permissions, so a partner isn't shown a switched-off Shield as a clean day",
];
const NEVER = [
  "Page addresses, searches, videos, titles or anything after the site name",
  "What you do inside an app: messages, posts, photos, notifications",
  "Keystrokes, passwords or what is on your screen",
  "Your location, contacts or files",
];

const PERMISSIONS = [
  ["Browser", "tabs, webNavigation", "To see which site the active tab is on, and to swap a blocked site for the block page.", "Reduced to the site name before anything is counted."],
  ["Browser", "idle", "Counts time only while you're at the computer (or something is playing).", "No camera, microphone or input is read."],
  ["Browser", "storage, alarms", "Keeps today's totals and syncs them every five minutes.", "Stored in the extension only."],
  ["Browser", "Site access: HustlrMate only", "Talks to the HustlrMate API, and lets hustlrmate.in connect the extension.", "It has no access to read or change other websites' content."],
  // The Android app's, for when it ships (marked "coming soon" in the table).
  ["Android", "Usage access (PACKAGE_USAGE_STATS)", "To know which app is in front, and to add up each app's daily time from Android's own records.", "You grant it in system settings; you can revoke it any time."],
  ["Android", "Display over other apps (SYSTEM_ALERT_WINDOW)", "To put the block screen over an app you chose to block.", "Only ever drawn over apps on your list."],
  ["Android", "Foreground service, special use", "Keeps the Shield running so blocks hold while the app is closed.", "Always visible as a \"Shield is on\" notification."],
  ["Android", "Run at start-up (RECEIVE_BOOT_COMPLETED)", "Turns the Shield back on after the phone restarts.", "Only if you had it on."],
  ["Android", "List of installed apps (launcher apps only)", "So you can pick apps from a list with their icons.", "The list stays on the phone; only apps with time on them are reported."],
];

const SHARING = [
  ["Nothing (the default)", "Partners can't see your Shield and aren't told when you take extra time."],
  ["My list", "Time and blocks on the sites and apps you listed, and any extra time you take."],
  ["Everything", "The above, plus time on every other site and app."],
];

const NOT_DONE = [
  "No accessibility service: it can't read or tap anything on your screen.",
  "No VPN and no network filtering: it never sees your traffic.",
  "No remote control: nobody else can block an app on your phone. Only you choose what's on your list.",
  "No ads or selling: Shield data is never used for advertising, never sold and never given to third parties.",
  "No hidden mode: on the phone it always shows a notification, and in the browser it's a visible extension you can remove.",
];

export default function ShieldAbout() {
  useEffect(() => {
    document.title = "How the Shield works | HustlrMate";
    return () => { document.title = "HustlrMate | Find your accountability partner"; };
  }, []);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-4xl px-6 sm:px-10">
        <div className="overline">Shield</div>
        <h1 className="mt-4 font-serif-editorial text-4xl leading-tight sm:text-6xl">How the Shield works, and what it sees.</h1>
        <p className="mt-6 max-w-3xl text-base font-light leading-8 text-[#A1A1AA] sm:text-lg">
          The Shield blocks or limits the websites you choose, and counts how long you spend on them, so you and a partner you trust can see the day honestly. It's a free browser extension, it's off until you connect it, it only blocks what's on your own list, and it records which site and for how long. Never what you did there. Phone apps are coming with the Android app.
        </p>

        <section className="mt-16 grid gap-4 md:grid-cols-2" aria-label="The two halves of the Shield">
          {HALVES.map(({ icon: Icon, name, where, steps, soon }) => (
            <div key={name} className={`border border-white/10 p-6 ${soon ? "opacity-70" : ""}`}>
              <div className="flex flex-wrap items-center gap-2"><Icon className="h-4 w-4 text-[#F59E0B]" /><h2 className="font-serif-editorial text-2xl">{name}</h2>{soon && <Soon />}</div>
              <p className="mt-1 text-xs text-[#777]">{where}</p>
              <ol className="mt-5 space-y-3 text-sm leading-relaxed text-[#B7B7BD]">
                {steps.map((step, index) => <li key={step} className="flex gap-3"><span className="w-4 shrink-0 tabular-nums text-[#777]">{index + 1}</span>{step}</li>)}
              </ol>
            </div>
          ))}
        </section>

        <section id="get-it" className="mt-16 scroll-mt-28 border-t border-white/10 pt-10">
          <h2 className="font-serif-editorial text-2xl sm:text-3xl">Get the extension</h2>
          <p className="mt-4 text-sm font-light leading-7 text-[#B7B7BD] sm:text-base">Free, for Chrome, Edge, Brave and other Chromium browsers on a computer. Phones and Safari aren't supported.</p>
          <ol className="mt-6 grid gap-3 text-sm leading-relaxed text-[#B7B7BD] sm:grid-cols-2">
            {(SHIELD_STORE_URL ? [
              "Open the store page and choose Add to Chrome (Get, in Edge). Confirm.",
              "A welcome page opens explaining what the Shield records. Nothing runs yet.",
              "Sign in at hustlrmate.in and open Shield from the menu.",
              "Tick the box agreeing to what it records, press Connect this browser, then add your first site.",
            ] : [
              "Download the extension (a .zip file) and unzip it into a folder you'll keep. The browser loads it from there.",
              "Type chrome://extensions (or edge://extensions) in the address bar and switch on Developer mode, top right.",
              "Press Load unpacked and choose the unzipped hustlrmate-shield folder. A welcome page opens.",
              "Sign in at hustlrmate.in, open Shield, press I've installed it, tick the box agreeing to what it records, and press Connect this browser.",
            ]).map((step, index) => <li key={step} className="flex gap-3 border border-white/10 p-4"><span className="w-4 shrink-0 tabular-nums text-[#F59E0B]">{index + 1}</span>{step}</li>)}
          </ol>
          <div className="mt-6 flex flex-wrap gap-3">
            {SHIELD_STORE_URL
              ? <a href={SHIELD_STORE_URL} target="_blank" rel="noreferrer" className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"><Download className="h-4 w-4" /> Add to browser</a>
              : <a href={SHIELD_DOWNLOAD} download className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold" data-testid="about-shield-download"><Download className="h-4 w-4" /> Download the extension</a>}
            <Link to="/shield" className="hm-btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm">Open my Shield <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {!SHIELD_STORE_URL && <p className="mt-4 text-xs leading-relaxed text-[#777]">The extension is on its way to the Chrome Web Store; until it's listed, this download is the same extension. Chrome may warn about developer-mode extensions, which is expected for extensions loaded this way. Keep the folder: deleting it removes the extension.</p>}
        </section>

        <Block title="What it records, and what it never does">
          <div className="grid gap-4 md:grid-cols-2">
            <List heading="Recorded" items={RECORDS} icon={Check} tone="text-[#F59E0B]" />
            <List heading="Never recorded" items={NEVER} icon={X} tone="text-[#F87171]" />
          </div>
        </Block>

        <Block title="Every permission, and why">
          <div className="overflow-x-auto border border-white/10">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="text-xs uppercase tracking-[0.12em] text-[#777]">
                <tr className="border-b border-white/10"><th className="px-4 py-3 font-normal">Where</th><th className="px-4 py-3 font-normal">Permission</th><th className="px-4 py-3 font-normal">Why</th><th className="px-4 py-3 font-normal">Limit</th></tr>
              </thead>
              <tbody className="text-[#B7B7BD]">
                {PERMISSIONS.map(([where, name, why, limit]) => (
                  <tr key={name} className="border-b border-white/[0.06] align-top last:border-0">
                    <td className="px-4 py-3 text-[#777]">{where === "Android" ? "Android app (coming soon)" : where}</td><td className="px-4 py-3 text-[#F5F5F0]">{name}</td><td className="px-4 py-3">{why}</td><td className="px-4 py-3">{limit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Block>

        <Block title="Who can see it">
          <p>You see everything your Shield records. A partner sees only what you choose, separately for each partnership, and you can change it any time:</p>
          <dl className="mt-4 grid gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-3">
            {SHARING.map(([level, meaning]) => <div key={level} className="bg-[#0A0A0A] p-4"><dt className="text-[#F5F5F0]">{level}</dt><dd className="mt-1 text-sm text-[#A1A1AA]">{meaning}</dd></div>)}
          </dl>
          <p>Partners you share with are told when you take extra time, remove something from your list, switch the Shield off, revoke a permission or remove the extension. That's the point: a Shield that can be quietly switched off doesn't help anyone. Partners you don't share with are told nothing.</p>
        </Block>

        <Block title="Where the data goes, and for how long">
          <ul className="space-y-2">
            <li>Daily totals go to HustlrMate's servers over an encrypted connection (HTTPS). The extension syncs every five minutes (the Android app, once it ships, every fifteen or so).</li>
            <li>Used only to enforce your limits across your devices and to show you, and partners you chose, how the day went.</li>
            <li>Daily time records are deleted after 60 days.</li>
            <li>Disconnecting a browser or phone from the Shield page deletes the time it recorded. Deleting your account deletes all Shield data. See <Link to="/delete-account">delete your account</Link>.</li>
          </ul>
        </Block>

        <Block title="Chrome Web Store user data policy">
          <p>HustlrMate Shield's use of information received from the browser complies with the <a href="https://developer.chrome.com/docs/webstore/program-policies/" target="_blank" rel="noreferrer">Chrome Web Store User Data Policy</a>, including the Limited Use requirements. Browsing data is used only to provide the Shield's blocking, limits and daily summaries described on this page. It is not transferred to third parties, not used for advertising, credit or lending, and not read by people except with your consent, for security, or where the law requires it.</p>
        </Block>

        <Block title="What the Shield doesn't do">
          <ul className="space-y-2">{NOT_DONE.map((item) => <li key={item}>{item}</li>)}</ul>
        </Block>

        <Block title="Turning it off">
          <ul className="space-y-2">
            <li><span className="text-[#F5F5F0]">Phone (once the app ships):</span> You › Shield › Turn off on this phone, or revoke usage access or display over other apps in Android settings, or uninstall the app.</li>
            <li><span className="text-[#F5F5F0]">Browser:</span> disconnect it from the Shield page, or remove the extension from the browser (right-click its icon, Remove from Chrome).</li>
            <li>Either way, blocking and counting stop at once.</li>
          </ul>
        </Block>

        <Block title="Questions">
          <p>Write to <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. More on how HustlrMate handles data in the <Link to="/privacy-policy">privacy policy</Link>.</p>
        </Block>
        <Link to="/auth" className="hm-btn-primary mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3.5 text-sm font-semibold">Get started <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </PublicLayout>
  );
}

function Soon() {
  return <span className="rounded-full border border-[#F59E0B]/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F59E0B]">Coming soon</span>;
}

function Block({ title, children }) {
  return (
    <section className="mt-16 border-t border-white/10 pt-10">
      <h2 className="font-serif-editorial text-2xl sm:text-3xl">{title}</h2>
      <div className="mt-5 space-y-4 text-sm font-light leading-7 text-[#B7B7BD] sm:text-base [&_a]:text-[#F59E0B] [&_a]:underline-offset-4 hover:[&_a]:underline">{children}</div>
    </section>
  );
}

function List({ heading, items, icon: Icon, tone }) {
  return (
    <div className="border border-white/10 p-5">
      <div className="overline mb-3">{heading}</div>
      <ul className="space-y-2 text-sm">{items.map((item) => <li key={item} className="flex gap-2"><Icon className={`mt-1 h-3.5 w-3.5 shrink-0 ${tone}`} />{item}</li>)}</ul>
    </div>
  );
}
